import { toast } from 'sonner';
import { aiGatewayService } from '@/services/aiGateway';
import { recordingSessionApi } from '@/services/recordingSessionApi';
import { drainIdbToRest } from '@/services/audioChunkDrain';
import {
  hasCompletionIntent,
  clearCompletionIntent,
} from '@/lib/completionIntent';
import { usePendingSyncStore } from '@/stores/usePendingSyncStore';

/**
 * Connection-restore drainer. Background worker that catches up the
 * doctor's recording backlog the moment the network is healthy again.
 *
 * The model:
 *   - In offline / flaky-net clinics doctors record N consults back-to-
 *     back. Each press of Complete persists `completionIntent` in
 *     localStorage and leaves a `paused/stopped` recording_sessions
 *     row server-side. Audio chunks live in IDB locally and (if the
 *     REST fallback could reach the server even briefly) in MinIO.
 *   - When the gateway reconnects (or the browser sees `online`), this
 *     drainer walks every session with a local completion intent,
 *     drains its IDB chunks to MinIO via REST, then asks the backend
 *     to reprocess. Server-side autosynth (and the auto-retry cron)
 *     are reachable for any session whose audio is ALREADY in MinIO,
 *     but only this drainer can pull chunks out of the doctor's IDB —
 *     so it's load-bearing for the offline-first story.
 *   - Sequential, not parallel. Whisper + the LLM are GPU-bound; ten
 *     concurrent reprocess calls would just bottleneck on the upstream
 *     and serialize anyway, while costing us extra HTTP timeouts.
 *   - Reports progress through `usePendingSyncStore` so the global
 *     `PendingSyncBanner` can show "3 of 10 recordings processing".
 *
 * Idempotent across rapid connect/disconnect bursts: a pass-in-flight
 * absorbs additional triggers. The marker keys are leftovers from the
 * existing localStorage scheme so this slots into the recovery story
 * the dock already implements; nothing new in storage.
 */

const COMPLETION_INTENT_PREFIX = 'maie.completionIntent.';

let passInFlight = false;
let queuedRetrigger = false;
let listenersWired = false;

interface DrainerCallbacks {
  /** Optional toast text overrides — keeps the i18n choice with the
   *  caller (App shell) instead of forking the locale plumbing into
   *  this module. */
  copy?: {
    syncStarting?: string;
    syncComplete?: string;
    syncPartial?: string;
  };
}

let callbacks: DrainerCallbacks = {};

const collectIntentSessionIds = (): string[] => {
  try {
    const ids: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(COMPLETION_INTENT_PREFIX)) {
        ids.push(key.slice(COMPLETION_INTENT_PREFIX.length));
      }
    }
    return ids;
  } catch {
    return [];
  }
};

const fetchOpenSessions = async (): Promise<
  Array<{
    id: string;
    status: string;
    medicalRecordId: string | null;
    patientNameEn?: string | null;
    patientNameAr?: string | null;
  }>
> => {
  try {
    const raw = await recordingSessionApi.mine();
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as { data?: unknown })?.data)
        ? ((raw as { data: unknown[] }).data as Array<{
            id: string;
            status: string;
            medicalRecordId: string | null;
            patientNameEn?: string | null;
            patientNameAr?: string | null;
          }>)
        : [];
    return list as Array<{
      id: string;
      status: string;
      medicalRecordId: string | null;
      patientNameEn?: string | null;
      patientNameAr?: string | null;
    }>;
  } catch {
    return [];
  }
};

/**
 * Run one drainer pass. Walks every session with local completion
 * intent, drains chunks + reprocesses each in turn, reporting to
 * the pending-sync store as it goes.
 *
 * Safe to call repeatedly — the in-flight guard dedupes; if you call
 * during an active pass we set `queuedRetrigger` and re-enter once
 * the current pass finishes (covers the case where new sessions get
 * intent-flagged mid-pass).
 */
export const runPendingSyncPass = async (): Promise<void> => {
  if (passInFlight) {
    queuedRetrigger = true;
    return;
  }
  passInFlight = true;
  try {
    do {
      queuedRetrigger = false;
      const intentIds = collectIntentSessionIds();
      if (intentIds.length === 0) break;
      // Cross-reference with the server: skip sessions that are already
      // committed/abandoned (the intent is stale; clear it) so we don't
      // pile on synth attempts the server has already concluded.
      const serverSessions = await fetchOpenSessions();
      const serverIndex = new Map(serverSessions.map((s) => [s.id, s]));
      const drainable = intentIds.filter((id) => {
        const s = serverIndex.get(id);
        if (!s) return false; // session vanished server-side — drop intent below
        if (s.status === 'committed' || s.status === 'abandoned') return false;
        if (s.medicalRecordId) return false;
        return true;
      });
      // Garbage-collect stale intents (committed/abandoned/missing).
      for (const id of intentIds) {
        const s = serverIndex.get(id);
        if (
          !s ||
          s.status === 'committed' ||
          s.status === 'abandoned' ||
          s.medicalRecordId
        ) {
          clearCompletionIntent(id);
        }
      }
      if (drainable.length === 0) break;

      const store = usePendingSyncStore.getState();
      store.beginPass();
      for (const sessionId of drainable) {
        const meta = serverIndex.get(sessionId);
        const patientName = meta?.patientNameEn || meta?.patientNameAr || '';
        store.enqueue({ sessionId, patientName, status: 'queued' });
      }

      let succeeded = 0;
      let failed = 0;
      if (callbacks.copy?.syncStarting) {
        toast.message(callbacks.copy.syncStarting, {
          id: 'pending-sync',
          duration: 4000,
        });
      }
      for (const sessionId of drainable) {
        try {
          // Re-check the intent: another tab may have cleared it
          // (manual discard from PendingRecordingsCard).
          if (!hasCompletionIntent(sessionId)) {
            usePendingSyncStore.getState().dismiss(sessionId);
            continue;
          }
          usePendingSyncStore.getState().setStatus(sessionId, 'uploading');
          const drain = await drainIdbToRest(sessionId);
          if (drain.failed > 0 && drain.uploaded === 0 && drain.total > 0) {
            // Drain truly failed (auth / persistent network failure).
            // Bail on this session; the next connect event retries.
            usePendingSyncStore
              .getState()
              .fail(
                sessionId,
                drain.firstError?.name,
                drain.firstError?.message ?? 'Audio upload failed',
              );
            failed += 1;
            continue;
          }
          usePendingSyncStore
            .getState()
            .setStatus(sessionId, 'reprocessing');
          await recordingSessionApi.reprocess(sessionId);
          usePendingSyncStore.getState().complete(sessionId);
          clearCompletionIntent(sessionId);
          succeeded += 1;
        } catch (err) {
          const e = err as {
            response?: {
              status?: number;
              data?: {
                code?: string;
                message?: string;
              };
            };
            message?: string;
          };
          const code = e?.response?.data?.code;
          const message =
            e?.response?.data?.message ?? e?.message ?? 'Reprocess failed';
          usePendingSyncStore.getState().fail(sessionId, code, message);
          // AUDIO_NOT_ON_SERVER on this device means IDB had nothing to
          // drain AND the server has nothing — the audio is genuinely
          // gone. Clear the intent so we stop trying on every reconnect;
          // the user will see the failed entry in the banner and can
          // discard from PendingRecordingsCard.
          if (code === 'AUDIO_NOT_ON_SERVER' || code === 'SYNTH_ABANDONED') {
            clearCompletionIntent(sessionId);
          }
          failed += 1;
        }
      }
      usePendingSyncStore.getState().endPass();

      if (callbacks.copy) {
        if (failed === 0 && succeeded > 0 && callbacks.copy.syncComplete) {
          toast.success(callbacks.copy.syncComplete, {
            id: 'pending-sync',
            description: `${succeeded}/${drainable.length}`,
            duration: 5000,
          });
        } else if (failed > 0 && callbacks.copy.syncPartial) {
          toast.warning(callbacks.copy.syncPartial, {
            id: 'pending-sync',
            description: `${succeeded}/${drainable.length}`,
            duration: 8000,
          });
        }
      }
    } while (queuedRetrigger);
  } finally {
    passInFlight = false;
  }
};

/**
 * Wire connection-event listeners. Idempotent — calling twice from a
 * StrictMode double-mount is fine.
 */
export const startPendingSyncDrainer = (cb: DrainerCallbacks = {}): void => {
  callbacks = cb;
  if (listenersWired) return;
  listenersWired = true;

  // Gateway socket up — full-stack ready for socket replay path inside
  // driveRecovery (preferred when chunks are in IDB) and reprocess
  // (preferred when chunks are already in MinIO).
  aiGatewayService.onConnectionChange((s) => {
    if (s === 'connected') {
      void runPendingSyncPass();
    }
  });

  // Browser network restore — fires before socket reconnect on flaky
  // networks. Even without the gateway, the REST drain path can reach
  // the API as long as HTTP works, and reprocess is REST too.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      void runPendingSyncPass();
    });
  }

  // First-mount kickoff. Covers the doctor opening the app to a
  // backlog of intent-flagged sessions from a prior offline run.
  // Defer one frame so the auth interceptors finish hydrating.
  setTimeout(() => {
    void runPendingSyncPass();
  }, 0);
};
